import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Bookmark, X, Trash2, Send, Flag, MoreVertical, Volume2, VolumeX, Maximize2, Play, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { getMediaUrl } from '../../utils/mediaUrl';
import { shareContent, buildShareUrl } from '../../utils/shareContent';
import { videoTexts } from './videoTranslations';
import VerifiedBadge from '../VerifiedBadge';
import MonetizationBadge from '../MonetizationBadge';
import SponsorshipBadge from '../SponsorshipBadge';
import SponsorPicker from '../SponsorPicker';
import { DollarSign } from 'lucide-react';

const fmt = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

const VideoCommentsModal = ({ videoId, onClose, onCommentAdded }) => {
  const { language } = useLanguage();
  const V = videoTexts(language);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`/api/videos/${videoId}/comments`).then((r) => setComments(r.data.comments || [])).catch(() => {});
  }, [videoId]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`/api/videos/${videoId}/comments`, { content: t });
      setComments((prev) => [r.data, ...prev]);
      setText('');
      onCommentAdded?.();
    } catch (e) { toast.error('Erreur'); } finally { setSending(false); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center" onClick={onClose} data-testid="video-comments-modal">
      <div className="bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-bold text-white">{V.comments}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="video-comments-close"><X className="w-5 h-5 text-white" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {comments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">{V.noComments}</p>
          ) : comments.map((c) => (
            <div key={c.id} className="bg-white/5 rounded-xl p-3" data-testid={`video-comment-${c.id}`}>
              <p className="text-xs text-fuchsia-300 font-semibold mb-1">{c.user?.name || c.user?.username || 'Utilisateur'}</p>
              <p className="text-sm text-gray-200 break-words">{c.content}</p>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-white/10 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder={V.addComment}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/60" data-testid="video-comment-input" />
          <button onClick={send} disabled={sending || !text.trim()} className="p-2.5 bg-fuchsia-600 rounded-full disabled:opacity-40" data-testid="video-comment-send"><Send className="w-4 h-4 text-white" /></button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Instagram-style video card: autoplay muted when ≥60% visible, tap = sound, progress bar seekable
export const VideoPostCard = ({ post, onUpdate, showDelete = false }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const V = videoTexts(language);
  const navigate = useNavigate();
  const [liked, setLiked] = useState(!!post.is_liked);
  const [showSponsorPicker, setShowSponsorPicker] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [saved, setSaved] = useState(!!post.is_saved);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(post.duration || 0);
  const [buffering, setBuffering] = useState(false);
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const viewedRef = useRef(false);
  const isOwner = user?.id === post.user_id;
  const ready = !post.processing && !post.failed && !!post.video_url;
  const portrait = post.width && post.height && post.height > post.width;

  useEffect(() => {
    const el = wrapRef.current;
    const vid = videoRef.current;
    if (!el || !vid || !ready) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio >= 0.6) {
        vid.play().catch(() => {});
      } else {
        vid.pause();
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, [ready]);

  const onTime = () => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    setProgress(vid.currentTime / vid.duration);
    if (!viewedRef.current && vid.currentTime > 2) {
      viewedRef.current = true;
      axios.post(`/api/videos/${post.id}/view`).catch(() => {});
    }
  };

  const seek = (e) => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));
    vid.currentTime = ratio * vid.duration;
    setProgress(ratio);
  };

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) vid.play().catch(() => {}); else vid.pause();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
    if (vid.paused) vid.play().catch(() => {});
  };

  const fullscreen = (e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.requestFullscreen) vid.requestFullscreen().catch(() => {});
    else if (vid.webkitEnterFullscreen) vid.webkitEnterFullscreen();
  };

  const toggleLike = async () => {
    try {
      const r = await axios.post(`/api/videos/${post.id}/like`);
      setLiked(r.data.liked);
      setLikesCount((p) => (r.data.liked ? p + 1 : Math.max(0, p - 1)));
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleSave = async () => {
    try {
      const r = await axios.post(`/api/videos/${post.id}/save`);
      setSaved(r.data.saved);
      toast.success(r.data.saved ? V.savedOk : V.removedSaved);
    } catch (e) { toast.error('Erreur'); }
  };

  const share = async () => {
    try {
      await shareContent({ title: 'Destine', text: V.shareText, url: buildShareUrl('video_post', post.id) });
      axios.post(`/api/videos/${post.id}/share`).catch(() => {});
    } catch (e) { /* cancelled */ }
  };

  const remove = async () => {
    if (!window.confirm(V.confirmDelete)) return;
    try {
      await axios.delete(`/api/videos/${post.id}`);
      toast.success(V.deleted);
      onUpdate?.();
    } catch (e) { toast.error('Erreur'); }
  };

  const report = async () => {
    const reason = window.prompt(V.reportPrompt);
    if (!reason || !reason.trim()) return;
    try {
      const form = new FormData();
      form.append('video_id', post.id);
      form.append('reason', reason.trim());
      await axios.post('/api/reports/video', form);
      toast.success(V.reported);
    } catch (e) { toast.error('Erreur'); }
  };

  const desc = post.description || '';
  const isLongDesc = desc.split('\n').length > 1 || desc.length > 90;

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden" data-testid="video-post-card">
      <div className="p-3 flex items-center gap-3">
        <button onClick={() => navigate(`/profile/${post.user?.id || post.user_id}`)} className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-fuchsia-600 rounded-full flex items-center justify-center overflow-hidden shrink-0">
          {post.user?.profile_image_url ? (
            <img src={getMediaUrl(post.user.profile_image_url)} alt={post.user?.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-white">{post.user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={() => navigate(`/profile/${post.user?.id || post.user_id}`)} className="flex items-center gap-1">
            <span className="font-semibold text-white truncate">{post.user?.name || 'Utilisateur'}</span>
            {post.user?.is_verified && <VerifiedBadge size={14} />}
          </button>
          <p className="text-xs text-gray-400 truncate">@{post.user?.username || 'user'}</p>
        </div>
        {isOwner && showDelete && (
          <button onClick={remove} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid="video-post-delete"><Trash2 className="w-4 h-4" /></button>
        )}
        <div className="relative">
          <button onClick={() => setShowMenu((v) => !v)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="video-post-menu-btn"><MoreVertical className="w-5 h-5" /></button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-9 z-30 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[170px]" data-testid="video-post-menu">
                <button onClick={() => { if (isOwner) return; setShowMenu(false); report(); }} disabled={isOwner}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${isOwner ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-orange-300 hover:bg-white/10'}`} data-testid="video-post-menu-report">
                  <Flag className="w-4 h-4" /> {V.report}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div ref={wrapRef} data-keep-dark="" className={`relative bg-black select-none ${portrait ? 'aspect-[4/5]' : 'aspect-video'}`} onClick={ready ? togglePlay : undefined} data-testid="video-post-player">
        {ready ? (
          <video
            ref={videoRef}
            src={getMediaUrl(post.video_url)}
            poster={post.thumbnail_url ? getMediaUrl(post.thumbnail_url) : undefined}
            className="w-full h-full object-contain"
            muted
            loop
            playsInline
            preload="metadata"
            onTimeUpdate={onTime}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || post.duration || 0)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onWaiting={() => setBuffering(true)}
            onPlaying={() => setBuffering(false)}
            onCanPlay={() => setBuffering(false)}
            data-testid="video-post-video"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2" data-testid="video-post-processing">
            {post.failed ? <span className="text-sm text-red-300">{V.failedBadge}</span> : (<><Loader2 className="w-7 h-7 animate-spin" /><span className="text-sm">{V.processingBadge}</span></>)}
          </div>
        )}
        {ready && !playing && !buffering && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center"><Play className="w-8 h-8 text-white ml-1" /></div>
          </div>
        )}
        {ready && buffering && playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>
        )}
        {ready && (
          <>
            <button onClick={toggleMute} className="absolute bottom-6 right-3 p-2 bg-black/60 rounded-full" title={V.tapUnmute} data-testid="video-post-mute">
              {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
            </button>
            <button onClick={fullscreen} className="absolute bottom-6 right-14 p-2 bg-black/60 rounded-full" data-testid="video-post-fullscreen"><Maximize2 className="w-4 h-4 text-white" /></button>
            <span className="absolute bottom-6 left-3 px-2 py-0.5 bg-black/60 rounded-full text-[11px] text-white font-mono" data-testid="video-post-time">{fmt(progress * duration)} / {fmt(duration)}</span>
            <div className="absolute bottom-0 left-0 right-0 h-5 flex items-end cursor-pointer" onClick={(e) => { e.stopPropagation(); seek(e); }} onTouchStart={(e) => { e.stopPropagation(); seek(e); }} onTouchMove={seek} data-testid="video-post-progress">
              <div className="w-full h-1.5 bg-white/25 relative">
                <div className="h-full bg-fuchsia-500" style={{ width: `${progress * 100}%` }} />
                <div className="absolute top-1/2 -translate-y-1/2 -ml-1.5 w-3 h-3 rounded-full bg-white shadow" style={{ left: `${progress * 100}%` }} />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="px-4 pt-3 pb-1 flex items-center gap-5">
        <button onClick={toggleLike} className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-pink-500' : 'text-gray-300 hover:text-pink-500'}`} data-testid="video-post-like">
          <Heart className={`w-6 h-6 ${liked ? 'fill-current' : ''}`} /><span className="text-sm">{likesCount}</span>
        </button>
        <button onClick={() => setShowComments(true)} className="flex items-center gap-1.5 text-gray-300 hover:text-fuchsia-400 transition-colors" data-testid="video-post-comments">
          <MessageCircle className="w-6 h-6" /><span className="text-sm">{commentsCount}</span>
        </button>
        <button onClick={share} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="video-post-share"><Share2 className="w-6 h-6" /></button>
        {/* v60.6 — badge monétisation toujours visible (explication 13 langues) */}
        <MonetizationBadge sponsored={!!post.sponsored_by?.sponsor_name} />
        {user && user.id === post.user_id && (
          <button onClick={() => setShowSponsorPicker(true)} className="text-gray-300 hover:text-emerald-400 transition-colors" title="Partager avec sponsor" aria-label="Partager avec sponsor" data-testid="short-video-sponsor-btn">
            <DollarSign className="w-6 h-6" />
          </button>
        )}
        <button onClick={toggleSave} className={`ml-auto transition-colors ${saved ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`} title={saved ? V.saved : V.save} data-testid="video-post-save">
          <Bookmark className={`w-6 h-6 ${saved ? 'fill-current' : ''}`} />
        </button>
      </div>

      {post.sponsored_by?.sponsor_name && (
        <div className="px-3 pb-2"><SponsorshipBadge sponsorship={post.sponsored_by} contentKind="video" compact /></div>
      )}
      <SponsorPicker
        open={showSponsorPicker}
        onClose={() => setShowSponsorPicker(false)}
        contentId={post.id}
        contentKind="video"
        onSubmitted={() => toast.success('Demande envoyée au sponsor')}
      />

      {desc && (
        <div className="px-4 pb-3">
          <p className={`text-sm text-gray-200 whitespace-pre-wrap break-words ${showFullDesc ? '' : 'line-clamp-1'}`} data-testid="video-post-description">
            <span className="font-semibold text-white mr-2">{post.user?.username || post.user?.name}</span>{desc}
          </p>
          {isLongDesc && (
            <button onClick={() => setShowFullDesc((v) => !v)} className="text-xs text-gray-400 hover:text-gray-200 mt-0.5" data-testid="video-desc-toggle">{showFullDesc ? V.seeLess : V.seeMore}</button>
          )}
        </div>
      )}

      {showComments && <VideoCommentsModal videoId={post.id} onClose={() => setShowComments(false)} onCommentAdded={() => setCommentsCount((p) => p + 1)} />}
    </div>
  );
};

export default VideoPostCard;
